import './SideNav.scss';
import { McIcon } from '@maersk-global/mds-react-wrapper';
import cx from 'classnames';
import { useState } from 'react';
import { AppRoutes } from '../../../../config/routes';
import { useGetCurrentRoute } from '../../hooks/useGetCurrentRoute';
import { AdjustableComponent, useClasses } from '../../hooks/useClasses';
import ChildrenPopup from './ChildrenPopup';

export type SideNavStyles = {
  root: string;
  item: string;
  itemSelected: string;
  itemDisabled: string;
  itemIcon: string;
  itemText: string;
};

export type SideNavItem = {
  title: string;
  route: AppRoutes;
  secondaryRoute?: AppRoutes;
  icon?: string;
  disabled?: boolean;
  separator?: boolean;
  onClick?: () => void;
  children?: SideNavItem[];
};

export type SideNavProps = {
  items: SideNavItem[];
};

const SideNav: AdjustableComponent<SideNavProps, SideNavStyles> = ({ items, classes }) => {
  const styles = useClasses(
    {
      root: 'SideNav',
      item: 'SideNav__item',
      itemSelected: 'SideNav__item--selected',
      itemDisabled: 'SideNav__item--disabled',
      itemIcon: 'SideNav__item-icon',
      itemText: 'SideNav__item-text',
    },
    classes,
  );

  const [showChildren, setShowChildren] = useState(false);
  const getCurrentRoute = useGetCurrentRoute();
  const currentRoute = getCurrentRoute();

  const getClassNamesForItem = (item: SideNavItem) =>
    cx(styles.item, {
      [styles.itemSelected]: currentRoute === item.route || currentRoute === item.secondaryRoute,
      [styles.itemDisabled]: item.disabled,
    });

  const onItemClick = (item: SideNavItem) => {
    if (item.disabled) {
      return;
    }
    if (item.onClick) {
      item.onClick();
      return;
    } else if (Boolean(item.children) && !showChildren) {
      setShowChildren(true);
    }
  };

  return (
    <ul className={styles.root}>
      {items.map((item, index) => (
        <li key={index} onClick={() => onItemClick(item)} className={getClassNamesForItem(item)}>
          {item.separator && <div className="SideNav__with-separator" />}
          {item.icon && (
            <div className={styles.itemIcon}>
              <McIcon size="24" icon={item.icon} />
            </div>
          )}
          <span className={styles.itemText}>{item.title}</span>
          {item.children && showChildren && (
            <ChildrenPopup items={item.children} onClose={() => setShowChildren(false)} />
          )}
        </li>
      ))}
    </ul>
  );
};

export default SideNav;
